import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { PageSpinner, Modal, EmptyState } from "../../components/ui.js";
import { IconClipboard } from "../../components/icons.js";

interface Item { name: string; calories: string; proteinG: string; carbsG: string; fatG: string }
interface Meal { id: string; name: string; totals: { calories: number; proteinG: number; carbsG: number; fatG: number }; items: { id: string; name: string }[] }

export function MealsTab() {
  const [meals, setMeals] = useState<Meal[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMeals((await api.get<{ meals: Meal[] }>("/api/meals")).meals);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load meals.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function remove(id: string) {
    await api.del(`/api/meals/${id}`).catch(() => undefined);
    await load();
  }

  if (!meals) return error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />;

  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted text-sm" style={{ margin: 0 }}>Saved meals you can log in one tap.</p>
        <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>New meal</button>
      </div>
      {meals.length === 0 ? (
        <EmptyState icon={<IconClipboard />} title="No saved meals yet">Build meals you eat often so logging and planning take one tap.</EmptyState>
      ) : (
        meals.map((m) => (
          <div className="card" key={m.id}>
            <div className="row-between">
              <div>
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div className="dim text-sm">{m.totals.calories} kcal · {m.totals.proteinG}g protein · {m.totals.carbsG}g carbs · {m.totals.fatG}g fat</div>
                <div className="dim text-xs mt-2">{m.items.map((i) => i.name).join(", ")}</div>
              </div>
              <button className="btn btn-sm btn-ghost" onClick={() => remove(m.id)}>Delete</button>
            </div>
          </div>
        ))
      )}
      {creating && <CreateMeal onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
    </div>
  );
}

function CreateMeal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [items, setItems] = useState<Item[]>([{ name: "", calories: "", proteinG: "", carbsG: "", fatG: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setItem(i: number, k: keyof Item, v: string) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  }

  async function save() {
    setError(null);
    const valid = items.filter((i) => i.name.trim());
    if (!name.trim() || valid.length === 0) {
      setError("Give the meal a name and at least one item.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/meals", {
        name,
        items: valid.map((i) => ({ name: i.name, calories: Number(i.calories) || 0, proteinG: Number(i.proteinG) || 0, carbsG: Number(i.carbsG) || 0, fatG: Number(i.fatG) || 0 }))
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setSaving(false);
    }
  }

  return (
    <Modal title="New meal" onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field">
        <label>Meal name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chicken, rice & broccoli" />
      </div>
      <label className="text-sm muted">Items</label>
      {items.map((it, i) => (
        <div className="card" key={i} style={{ background: "var(--surface-1)", marginTop: "var(--space-2)", padding: "var(--space-3)" }}>
          <input className="input" value={it.name} placeholder="Item name" onChange={(e) => setItem(i, "name", e.target.value)} />
          <div className="row wrap mt-2">
            {(["calories", "proteinG", "carbsG", "fatG"] as (keyof Item)[]).map((k) => (
              <input key={k} className="input grow" type="number" inputMode="decimal" style={{ minWidth: 70 }}
                placeholder={k === "calories" ? "kcal" : k === "proteinG" ? "P" : k === "carbsG" ? "C" : "F"}
                value={it[k]} onChange={(e) => setItem(i, k, e.target.value)} />
            ))}
          </div>
        </div>
      ))}
      <button className="btn btn-sm mt-4" onClick={() => setItems([...items, { name: "", calories: "", proteinG: "", carbsG: "", fatG: "" }])}>+ Add item</button>
      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save meal"}</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}
